import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomModelConfigurationPanel from './CustomModelConfigurationPanel';
import {
    buildCustomModelValue,
    buildCustomProviderOptionValue,
    getCustomProvider,
    isCustomProviderOptionValue,
    normalizeCustomModelConfig,
    parseCustomModelValue,
    parseCustomProviderOptionValue,
} from './modelConfig';

/**
 * Keeps the legacy item model select working while custom models are stored via hidden canonical inputs.
 */
export default function ItemCustomModelManager() {
    const rootRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const [selectedValue, setSelectedValue] = useState('');
    const [customProvider, setCustomProvider] = useState(null);
    const [customModelName, setCustomModelName] = useState('');
    const [customConfig, setCustomConfig] = useState({});

    const buildLegacyFlagsForCustomModel = (config) => {
        const normalizedConfig = normalizeCustomModelConfig(config);
        const outputCapabilities = Array.isArray(normalizedConfig.model_capabilities?.out)
            ? normalizedConfig.model_capabilities.out
            : [];

        return {
            prompt: 1,
            captioning: outputCapabilities.includes('text') ? 1 : 0,
            model_image_prompt: 0,
        };
    };

    const domRefs = useMemo(() => {
        const select = document.getElementById('input-model');
        const hiddenModelInput = document.getElementById('input-model-value');
        const hiddenConfigInput = document.getElementById('input-model-config');

        return { select, hiddenModelInput, hiddenConfigInput };
    }, []);

    useEffect(() => {
        const root = document.getElementById('item-model-custom-root');
        rootRef.current = root;
        if (!root || !domRefs.select || !domRefs.hiddenModelInput || !domRefs.hiddenConfigInput) {
            return;
        }

        const canManageCustomModels = root.dataset.canManageCustomModels === 'true' && root.dataset.disabled !== 'true';
        const initialModelValue = domRefs.hiddenModelInput.value || domRefs.select.value || '';
        const parsedCustomModel = parseCustomModelValue(initialModelValue);
        const visibleProviders = canManageCustomModels
            ? ['vllm', 'ollama']
            : (parsedCustomModel ? [parsedCustomModel.provider] : []);

        visibleProviders.forEach((provider) => {
            const optionValue = buildCustomProviderOptionValue(provider);
            if (!Array.from(domRefs.select.options).some((option) => option.value === optionValue)) {
                const providerInfo = getCustomProvider(provider);
                const option = new Option(providerInfo?.label || provider, optionValue, false, false);
                option.dataset.customProvider = provider;
                option.dataset.prompt = '0';
                option.dataset.captioning = '0';
                option.dataset.model_image_prompt = '0';
                option.disabled = !canManageCustomModels;
                domRefs.select.add(option);
            }
        });

        let initialConfig = {};
        try {
            initialConfig = JSON.parse(root.dataset.initialConfig || '{}') || {};
        } catch (error) {
            initialConfig = {};
        }

        if (parsedCustomModel) {
            setSelectedValue(buildCustomProviderOptionValue(parsedCustomModel.provider));
            setCustomProvider(parsedCustomModel.provider);
            setCustomModelName(parsedCustomModel.modelName);
            setCustomConfig(normalizeCustomModelConfig(initialConfig));
        } else {
            setSelectedValue(initialModelValue);
            setCustomProvider(null);
            setCustomModelName('');
            setCustomConfig({});
        }

        setIsReady(true);
    }, [domRefs.hiddenConfigInput, domRefs.hiddenModelInput, domRefs.select]);

    useEffect(() => {
        if (!isReady || !domRefs.select || !domRefs.hiddenModelInput || !domRefs.hiddenConfigInput) {
            return;
        }

        const isCustomSelection = isCustomProviderOptionValue(selectedValue) || Boolean(customProvider);
        const provider = customProvider || parseCustomProviderOptionValue(selectedValue);
        const config = normalizeCustomModelConfig(customConfig);
        if (rootRef.current?.dataset.disabled === 'true') {
            return;
        }
        const actualModelValue = isCustomSelection
            ? buildCustomModelValue(provider, customModelName)
            : selectedValue;

        domRefs.hiddenModelInput.value = actualModelValue;
        domRefs.hiddenConfigInput.value = isCustomSelection ? JSON.stringify(config) : '';

        const nextSelectValue = isCustomSelection ? buildCustomProviderOptionValue(provider) : selectedValue;
        if (nextSelectValue && domRefs.select.value !== nextSelectValue) {
            domRefs.select.value = nextSelectValue;
        }

        if (isCustomSelection) {
            const selectedOption = Array.from(domRefs.select.options).find((option) => option.value === nextSelectValue);
            if (selectedOption) {
                const flags = buildLegacyFlagsForCustomModel(config);
                selectedOption.dataset.prompt = String(flags.prompt);
                selectedOption.dataset.captioning = String(flags.captioning);
                selectedOption.dataset.model_image_prompt = String(flags.model_image_prompt);
                selectedOption.dataset.actualModel = actualModelValue;
            }
        }

        if (typeof window.$ !== 'undefined' && typeof window.$(domRefs.select).selectpicker === 'function') {
            window.$(domRefs.select).selectpicker('val', nextSelectValue || '');
            window.$(domRefs.select).selectpicker('refresh');
        }

        domRefs.select.dispatchEvent(new Event('change', { bubbles: true }));
    }, [customConfig, customModelName, customProvider, domRefs.hiddenConfigInput, domRefs.hiddenModelInput, domRefs.select, isReady, selectedValue]);

    useEffect(() => {
        if (!isReady || !domRefs.select) {
            return undefined;
        }

        const handleSelectChange = (event) => {
            const nextValue = event.target.value || '';
            setSelectedValue(nextValue);

            if (isCustomProviderOptionValue(nextValue)) {
                const provider = parseCustomProviderOptionValue(nextValue);
                setCustomProvider(provider);
                setCustomConfig((prev) => normalizeCustomModelConfig(prev));
                if (provider !== customProvider) {
                    setCustomModelName('');
                }
                return;
            }

            setCustomProvider(null);
            setCustomModelName('');
            setCustomConfig({});
        };

        domRefs.select.addEventListener('change', handleSelectChange);
        return () => {
            domRefs.select.removeEventListener('change', handleSelectChange);
        };
    }, [customProvider, domRefs.select, isReady]);

    if (!isReady) {
        return null;
    }

    if (rootRef.current?.dataset.canManageCustomModels !== 'true') {
        return null;
    }

    return (
        <CustomModelConfigurationPanel
            provider={customProvider}
            modelName={customModelName}
            config={customConfig}
            disabled={rootRef.current?.dataset.disabled === 'true'}
            title="Flux custom model"
            onModelNameChange={setCustomModelName}
            onConfigChange={setCustomConfig}
        />
    );
}
